import { USER_KEY } from 'constants/keys'
import useAuthentication from 'hooks/use-auth'
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import api from 'services/api'
import useOrganizationStore from 'store/header-organization'
import { ISignInCredentials, IUser } from 'types/user'
import generateThemeResponse from 'utils/user/generate-theme-response'
import { sessaoPronta } from './sessao-pronta'
import { useCustomTheme } from './theme'

interface IAuthContextData {
  user: IUser | null
  token: string
  isAuthenticated: boolean
  isExternalAuth: boolean
  signIn(credentials: ISignInCredentials): Promise<IUser>
  signInWithGoogle(code: string): Promise<IUser>
  signInWithoutStorage(credentials: ISignInCredentials): Promise<IUser>
  signInUpdate(token: string, user: IUser): Promise<void>
  signOut(): void
  skipUploadCertificate(): void
  backToCertificadeUpload(): void
  updateUser(user: IUser): void
  updateUserData(): Promise<void>
  changeOrganization(organizationId: string): Promise<void>
  setIsExternalAuth: Dispatch<SetStateAction<boolean>>
  updateOnCertificateUpload: () => Promise<void>
}

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData)

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [isExternalAuth, setIsExternalAuth] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const { token, user, updateToken, updateUser, updateUserWithStorage, clearStorage } = useAuthentication()
  const { onChangeThema, onResetThema } = useCustomTheme()

  const { setOrganization } = useOrganizationStore((state) => ({
    setOrganization: state.setOrganization,
  }))

  const history = useHistory()

  const processAuthentication = useCallback(
    async (user: any, token: string) => {
      if (!user.isSeller && user.theme != null) {
        onChangeThema(generateThemeResponse(user))
      }

      updateUser(user)
      updateToken(token)

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`

      if (user.organization) {
        setIsAuthenticated(true)
        setOrganization({
          id: user.organization.id ?? '',
          name: user.organization.name!,
          federalTaxId: user.organization.cnpj!,
        })
      } else {
        setIsAuthenticated(false)
      }

      return user
    },
    [
      user,
      token,
      updateUser,
      updateToken,
      onChangeThema,
      setIsAuthenticated,
      generateThemeResponse,
      api,
      setOrganization,
    ]
  )

  const signIn = useCallback(
    async ({ userId, email, password }: ISignInCredentials): Promise<IUser> => {
      try {
        const response = await api.post('sessions', {
          userId,
          email,
          password,
        })

        const { user, token } = response.data

        return processAuthentication(user, token)
      } catch (err: any) {
        throw err?.response?.data ?? 'Erro desconhecido'
      }
    },
    [processAuthentication, clearStorage]
  )

  const signInWithGoogle = useCallback(
    async (code: string): Promise<IUser> => {
      try {
        const response = await api.post('/sessions/google', {
          code: code,
        })

        const { user, token } = response.data

        return processAuthentication(user, token)
      } catch (err: any) {
        throw err?.response?.data ?? 'Erro desconhecido'
      }
    },
    [clearStorage]
  )

  const signOut = useCallback(() => {
    clearStorage()
    setIsAuthenticated(false)
    onResetThema()
  }, [clearStorage, setIsAuthenticated, onResetThema])

  const signInWithoutStorage = useCallback(async ({ userId, email, password }: ISignInCredentials): Promise<IUser> => {
    try {
      const response = await api.post('sessions', {
        userId,
        email,
        password,
      })

      const { token, user } = response.data

      api.defaults.headers.common['Authorization'] = `Bearer ${token}`

      updateUser(user)
      updateToken(token)

      return user
    } catch (err: any) {
      throw err?.response?.data ?? 'Erro desconhecido'
    }
  }, [])

  const signInUpdate = useCallback(async (token: string, user: IUser) => {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`

    updateUser(user)
    updateToken(token)
  }, [])

  const skipUploadCertificate = useCallback(() => {
    const userString = localStorage.getItem(USER_KEY)
    const user = JSON.parse(userString as string)

    user.skipUpload = true

    updateUser(user)
  }, [])

  const backToCertificadeUpload = useCallback(() => {
    const userString = localStorage.getItem(USER_KEY)
    const user = JSON.parse(userString as string)

    user.skipUpload = false

    updateUser(user)
  }, [user])

  const validate = useCallback(async () => {
    if (user && token) {
      if (!user.isSeller && user.theme != null) {
        onChangeThema(generateThemeResponse(user))
      }

      if (user.organization) {
        setOrganization({
          id: user.organization.id ?? '',
          name: user.organization.name!,
          federalTaxId: user.organization.cnpj!,
        })
      }

      return setIsAuthenticated(sessaoPronta(user, token))
    }

    const queryParameters = new URLSearchParams(window.location.search)
    const tokenInRoute = queryParameters.get('token')

    if (!tokenInRoute) {
      return setIsAuthenticated(false)
    }

    if (token === '') {
      setIsAuthenticated(false)
    } else {
      api.defaults.headers.common['Authorization'] = `Bearer ${tokenInRoute}`
      setIsAuthenticated(true)
    }
  }, [user, isAuthenticated, setOrganization])

  const updateUserData = useCallback(async () => {
    try {
      const { data } = await api.get('/users/profile')

      const user = data.data

      if (!user.isSeller && user.theme != null) {
        onChangeThema(generateThemeResponse(user))
      }

      updateUserWithStorage(user)
    } catch (error) {
      console.log(error)
    }
  }, [updateUserWithStorage, onChangeThema, generateThemeResponse])

  const updateOnCertificateUpload = async () => {
    try {
      const { data } = await api.get('/users/profile')

      const user = data.data

      // if (!user.isSeller && user.theme != null) {
      //   onChangeThema(generateThemeResponse(user))
      // }

      updateUserWithStorage(user)
    } catch (error) {
      console.log(error)
    }
  }

  const onChangeOrganization = async (organizationId: string) => {
    try {
      const response = await api.post('/choose-organization', {
        organizationId: organizationId,
      })

      const { user, token } = response.data

      updateToken(token)
      updateUser(user)

      if (user.organization) {
        setOrganization({
          id: user.organization.id ?? '',
          name: user.organization.name!,
          federalTaxId: user.organization.cnpj!,
        })
      }

      setIsAuthenticated(true)

      history.push('/redirect')
    } catch (error) {
      console.log(error)
    }
  }

  async function update(signal: AbortSignal) {
    try {
      const { data } = await api.get('/users/profile', { signal })

      const user = data.data

      if (!user.isSeller && user.theme != null) {
        onChangeThema(generateThemeResponse(user))
      }

      updateUserWithStorage(user)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    // Aplica o tema imediatamente se já existir no localStorage
    if (user && !user.isSeller && user.theme != null) {
      onChangeThema(generateThemeResponse(user))
    }

    validate()

    if (token) {
      update(signal)
    }

    return () => {
      controller.abort()
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user: user,
        token: token,
        isAuthenticated,
        isExternalAuth,
        signIn,
        signInWithGoogle,
        signInWithoutStorage,
        signInUpdate,
        signOut,
        skipUploadCertificate,
        backToCertificadeUpload,
        updateUserData,
        updateUser,
        setIsExternalAuth,
        changeOrganization: onChangeOrganization,
        updateOnCertificateUpload,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext)

  return context
}

export { AuthProvider, useAuth }
